import {useRouteLoaderData} from "react-router";

import en from "./en";
import fr from "./fr";
import ro from "./ro";

export type GlobalStrings = typeof fr;
export type GlobalLocale = "en" | "fr" | "ro";

export const supportedLocales: GlobalLocale[] = ["en", "fr", "ro"];

export const globalStringsByLocale: Record<GlobalLocale, GlobalStrings> = {
  en,
  fr,
  ro,
};

export const defaultLocale: GlobalLocale = "fr";

export function isSupportedLocale(value: string | null | undefined): value is GlobalLocale {
  return Boolean(value) && supportedLocales.includes(value as GlobalLocale);
}

export function resolveLocale(locale?: string | null): GlobalLocale {
  if (!locale) return defaultLocale;
  const normalized = locale.toLowerCase();
  const tokens = normalized.split(",").map((value) => value.trim());
  for (const token of tokens) {
    const [code] = token.split(";");
    const base = code.split("-")[0] as GlobalLocale;
    if (base in globalStringsByLocale) {
      return base;
    }
  }
  return defaultLocale;
}

export function resolveLocaleFromPathname(pathname: string) {
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  return isSupportedLocale(firstSegment) ? firstSegment : defaultLocale;
}

export function resolveLocaleFromRequest(request: Request) {
  const url = new URL(request.url);
  const pathLocale = resolveLocaleFromPathname(url.pathname);
  if (isSupportedLocale(pathLocale)) {
    return pathLocale;
  }
  const queryLocale = url.searchParams.get("lang");
  if (isSupportedLocale(queryLocale)) {
    return queryLocale;
  }
  return resolveLocale(request.headers.get("accept-language"));
}

export function getGlobalStrings(locale?: string | null) {
  return globalStringsByLocale[resolveLocale(locale)] ?? fr;
}

export function useGlobalStrings() {
  const rootData = useRouteLoaderData("root") as {locale?: string} | undefined;
  const browserLocale = typeof navigator !== "undefined" ? navigator.language : undefined;
  return getGlobalStrings(rootData?.locale ?? browserLocale ?? defaultLocale);
}

export {en, fr, ro};
export default fr;
